import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  getBrandLogoLinkUrl,
  getMainNavigation,
  getShowMinimalNavigation,
  getShowMinimalNavigationHelp,
  getShowNavigation,
  getTopNavigation,
} from '@core/jss-routing';
import {
  NavigationEssentModule,
  NavigationService,
} from '@core/navigation-legacy';
import { getEcmpLogo, helpClickedAction } from '@core/navigation-store';
import { getLogoHomepageNavigationLabel } from '@core/navigation-ui';
import { NavigationDesktopComponent } from '@core/navigation-ui/navigation-desktop';
import { NavigationDomainLinksComponent } from '@core/navigation-ui/navigation-domain-links';
import { NavigationEcmpLogoComponent } from '@core/navigation-ui/navigation-ecmp-logo';
import { NavigationHeaderComponent } from '@core/navigation-ui/navigation-header';
import { NavigationHelpNeededButtonComponent } from '@core/navigation-ui/navigation-help-needed-button';
import { NavigationLogoComponent } from '@core/navigation-ui/navigation-logo';
import { NavigationMobileComponent } from '@core/navigation-ui/navigation-mobile';
import { NavigationServiceButtonComponent } from '@core/navigation-ui/navigation-service-button';
import { SkipLinkComponent } from '@core/navigation-ui/skip-link';
import { loginUrl } from '@core/settings';
import {
  CrntButton,
  CrntIcon,
  CrntIconButton,
  CrntVisuallyHidden,
} from '@essent/current-web-angular';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { DeployUrlPipe } from '@innogy/utils/environment';
import { GenericModalSources } from '@legacy/common-ui-shared-interfaces';
import { Store } from '@ngrx/store';
import { SearchBarComponent, SearchTriggerComponent } from '@search/ui';

@Component({
  selector: 'wl-header-open-essent-container',
  standalone: true,
  imports: [
    NavigationEssentModule,
    SkipLinkComponent,
    NgTemplateOutlet,
    NavigationHeaderComponent,
    NavigationServiceButtonComponent,
    NavigationDomainLinksComponent,
    NavigationDesktopComponent,
    NavigationLogoComponent,
    NavigationEcmpLogoComponent,
    NavigationMobileComponent,
    NavigationHelpNeededButtonComponent,
    SearchBarComponent,
    SearchTriggerComponent,
    AutomationIdDirective,
    CrntButton,
    CrntIcon,
    CrntIconButton,
    CrntVisuallyHidden,
  ],
  providers: [DeployUrlPipe],
  templateUrl: './header-open-essent-container.component.html',
})
export class HeaderOpenEssentContainerComponent {
  readonly #store = inject(Store);
  readonly #config = inject(ENVIRONMENT_CONFIG);
  readonly #navigationService = inject(NavigationService);
  readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('desktopSearchTriggerEl')
  desktopSearchTriggerEl!: SearchTriggerComponent;
  @ViewChild('mobileSearchTriggerEl', { read: ElementRef })
  mobileSearchTriggerEl!: ElementRef;

  readonly logoLabel = signal(getLogoHomepageNavigationLabel(this.#config));
  logoUrl = this.#store.selectSignal(getBrandLogoLinkUrl);

  readonly ecmpLogo = this.#store.selectSignal(getEcmpLogo);

  loginLinkField = this.#store.selectSignal(loginUrl);
  readonly loginUrl = computed(() => this.loginLinkField()?.value.href);
  readonly loginText = computed(() => this.loginLinkField()?.label);
  minimalNavigationActive = this.#store.selectSignal(getShowMinimalNavigation);
  readonly topNavigation = toSignal(this.#store.pipe(getTopNavigation));
  readonly mainNavigation = toSignal(this.#store.pipe(getMainNavigation));
  navigationActive = this.#store.selectSignal(getShowNavigation);
  readonly sitecoreDataLoaded = computed(
    () => !!this.mainNavigation()?.isNavigationAvailable
  );

  showMinimalNavigationHelp = this.#store.selectSignal(
    getShowMinimalNavigationHelp
  );
  readonly showNavigation = computed(
    () => this.navigationActive() && this.sitecoreDataLoaded()
  );
  readonly shownEcmpLogo = computed(() =>
    this.minimalNavigationActive() && this.ecmpLogo()?.logo
      ? this.ecmpLogo()
      : null
  );
  readonly showLoginButton = computed(
    () => this.loginUrl() && this.loginText()
  );
  readonly showMainNavigation = computed(() => !this.minimalNavigationActive());

  enableA11yHeader = this.#config.a11y.enableA11YHeader;
  enableSearch = this.#config.search.enabled;

  searchbarVisible = this.#navigationService.searchNavigationEnabled;

  onSearchFocusRemoved() {
    this.searchbarVisible.set(false);

    // NOTE: When this function is called, the desktopSearchTrigger may not be visible yet.
    // We need to wait for the next "render"
    this.changeDetectorRef.detectChanges();
    this.desktopSearchTriggerEl?.setFocus();

    // NOTE2: since the mobileSearchTriggerEl is a web-component, it is not immediately focusable when the angular
    // rendering is complete because only the empty element is in the DOM, but the web-component is not "connected" yet.
    // As soon as it is added, some code is queued in the call-stack that will "connect" it to the DOM.
    // We can only focus the element once this call-stack is done. So we use Promise.resolve() here to make
    // sure this code is executed afterward.
    Promise.resolve().then(() =>
      this.mobileSearchTriggerEl?.nativeElement?.focus()
    );
    // NOTE3: When the platform is on Angular >=19.1, we can use afterNextRender() for both of these cases.
  }

  dispatchHelpAction() {
    this.#store.dispatch(
      helpClickedAction({
        payload: {
          source: GenericModalSources.CHATBOT,
        },
      })
    );
  }
}
